/**
 * Edit one written page — a policy, About us, or the FAQ's heading and SEO.
 *
 * Both languages sit side by side, because that is how they are checked: a
 * policy whose Arabic says something different from its English is the
 * mistake this screen most needs to make visible.
 *
 * The SEO fields start empty on purpose. When they are empty the storefront
 * writes a title and description from the page itself, and that automatic
 * text is shown here in grey, so an admin only types over it when they want
 * something better. Nothing generated is ever saved.
 *
 * A save sends only the fields that were changed. The backend leaves the rest
 * alone, so two admins working on different fields of the same page do not
 * overwrite each other.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Save } from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import RichTextField from "./RichTextField";
import FaqTreeEditor from "./FaqTreeEditor";
import {
  PAGE_PATHS,
  PAGE_STATUS_OPTIONS,
  SEO_SOFT_LIMITS,
  SITE,
  errorText,
  todayISO,
} from "./shared";
import "../DynamicSections/HomePageCRM.css";
import "./ContentPages.css";

// The fields this form edits, exactly as the API names them.
const FIELDS = [
  "title", "ar_title",
  "seo_title", "ar_seo_title",
  "meta_description", "ar_meta_description",
  "body_html", "ar_body_html",
  "status", "is_indexable", "last_updated",
];

const toForm = (page) => ({
  title: page.title || "",
  ar_title: page.ar_title || "",
  seo_title: page.seo_title || "",
  ar_seo_title: page.ar_seo_title || "",
  meta_description: page.meta_description || "",
  ar_meta_description: page.ar_meta_description || "",
  body_html: page.body_html || "",
  ar_body_html: page.ar_body_html || "",
  status: page.status_label || "draft",
  is_indexable: Boolean(page.is_indexable),
  last_updated: page.last_updated || "",
});

const Counter = ({ value, max }) => {
  const length = (value || "").length;
  const state = length > max ? "over" : length > max * 0.9 ? "near" : "ok";
  return <span className={`cp-counter cp-counter--${state}`}>{length}/{max}</span>;
};

const GooglePreview = ({ locale, path, title, description, rtl }) => (
  <div className={`cp-google${rtl ? " cp-google--rtl" : ""}`}>
    <div className="cp-google__url">{SITE} › {locale}{path}</div>
    <div className="cp-google__title">{title || "—"}</div>
    <div className="cp-google__desc">{description || "No description yet."}</div>
  </div>
);

const ContentPageForm = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const { hasAction } = usePermissions();
  const editable = hasAction(MENU.SETTINGS, PERMISSION.EDIT);
  const isFaq = key === "faq";

  const [page, setPage] = useState(null); // the last version read from the server
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState("");
  // The FAQ's questions are saved separately (FaqTreeEditor); the page still
  // has to know about them to warn before leaving with unsaved questions.
  const [faqDirty, setFaqDirty] = useState(false);
  const pageRef = useRef(null);
  pageRef.current = page;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`admin/pages/${key}`);
      const response = data?.response;
      setPage(response);
      setForm(toForm(response));
      setErrors("");
    } catch (error) {
      swal({ text: errorText(error, "Could not load this page"), icon: "error" });
      navigate("/content-pages");
    } finally {
      setLoading(false);
    }
  }, [key, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = useMemo(() => {
    if (!page || !form) return {};
    const original = toForm(page);
    return FIELDS.reduce((acc, field) => {
      if (form[field] !== original[field]) acc[field] = form[field];
      return acc;
    }, {});
  }, [page, form]);
  const dirty = Object.keys(changed).length > 0;
  const anythingUnsaved = dirty || faqDirty;

  // Leaving with unsaved edits asks first — a policy half-rewritten and lost
  // is an afternoon gone.
  useEffect(() => {
    if (!anythingUnsaved) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anythingUnsaved]);

  const set = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));
  const setFromInput = (field) => (event) => set(field)(event.target.value);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setErrors("The English title is required.");
      return;
    }
    const payload = { ...changed };
    // Blank dates and SEO fields mean "not set", which the API reads as null.
    if ("last_updated" in payload && !payload.last_updated) payload.last_updated = null;

    setSaving(true);
    setErrors("");
    try {
      const { data } = await axiosInstance.put(`admin/pages/${key}`, payload);
      const response = data?.response;
      setPage(response);
      setForm(toForm(response));
      swal({ text: "Page saved", icon: "success", timer: 1400, buttons: false });
    } catch (error) {
      setErrors(errorText(error, "Could not save the page"));
    } finally {
      setSaving(false);
    }
  };

  // A questions save moves the FAQ's printed date. Show the new date, unless
  // the admin is part-way through changing that field themselves.
  const handleFaqSaved = useCallback((response) => {
    const before = pageRef.current?.last_updated || "";
    const after = response?.last_updated || "";
    setPage((current) => ({ ...current, last_updated: response?.last_updated }));
    setForm((current) => (current.last_updated === before ? { ...current, last_updated: after } : current));
  }, []);

  const handleBack = async () => {
    if (anythingUnsaved) {
      const leave = await swal({
        text: "You have unsaved changes. Leave without saving?",
        icon: "warning",
        buttons: ["Stay", "Leave"],
        dangerMode: true,
      });
      if (!leave) return;
    }
    navigate("/content-pages");
  };

  if (loading || !form) {
    return (
      <div className="ds-container">
        <div className="ds-empty-state" style={{ height: 200 }}>Loading page...</div>
      </div>
    );
  }

  const path = PAGE_PATHS[key] || `/${key}`;
  const preview = page.seo_preview || {};

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <button type="button" className="ds-btn-secondary" onClick={handleBack} style={{ marginBottom: 8 }}>
            <ArrowLeft size={14} style={{ marginInlineEnd: 4 }} />
            Pages
          </button>
          <h4>{page.title}</h4>
          <p>{SITE}/en{path} · {SITE}/ar{path}</p>
        </div>
      </div>

      <form className="ds-form-container" onSubmit={handleSave}>
        <fieldset disabled={!editable || saving} style={{ border: 0, padding: 0, margin: 0 }}>
          {errors && <div className="cp-errors">{errors}</div>}

          {/* ── Page ── */}
          <div className="ds-form-section">
            <h5>Page</h5>
            <div className="cp-lang-grid">
              <div className="ds-form-group">
                <label htmlFor="cp-title">Title (English) *</label>
                <input id="cp-title" type="text" value={form.title} onChange={setFromInput("title")} maxLength={255} />
              </div>
              <div className="ds-form-group">
                <label htmlFor="cp-ar_title">Title (Arabic)</label>
                <input
                  id="cp-ar_title"
                  type="text"
                  dir="rtl"
                  value={form.ar_title}
                  onChange={setFromInput("ar_title")}
                  maxLength={255}
                  placeholder="يُعرض العنوان الإنجليزي إذا تُرك فارغًا"
                />
              </div>
            </div>

            <div className="ds-form-grid" style={{ marginTop: 12 }}>
              <div className="ds-form-group">
                <label htmlFor="cp-status">Status</label>
                <select id="cp-status" value={form.status} onChange={setFromInput("status")}>
                  {PAGE_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label} — {option.hint}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ds-form-group">
                <label>Show this page on Google</label>
                <div className="ds-toggle-row">
                  <label className="ds-toggle">
                    <input
                      id="cp-is_indexable"
                      type="checkbox"
                      aria-label="Show this page on Google"
                      checked={form.is_indexable}
                      onChange={(event) => set("is_indexable")(event.target.checked)}
                    />
                    <span className="ds-toggle-slider" />
                  </label>
                  <span style={{ fontSize: 13 }}>
                    {form.is_indexable ? "Shown in search results" : "Hidden from search results"}
                  </span>
                </div>
                <span className="ds-hint">For the SEO team. The page stays live either way.</span>
              </div>

              <div className="ds-form-group">
                <label htmlFor="cp-last_updated">Last updated (shown to customers)</label>
                <div className="cp-date-row">
                  <input
                    id="cp-last_updated"
                    type="date"
                    value={form.last_updated}
                    onChange={setFromInput("last_updated")}
                  />
                  <button type="button" className="ds-btn-secondary" onClick={() => set("last_updated")(todayISO())}>
                    <CalendarDays size={14} style={{ marginInlineEnd: 4 }} />
                    Today
                  </button>
                </div>
                <span className="ds-hint">
                  {isFaq
                    ? "Moves on its own when the questions change."
                    : "Change it when the policy itself changes — not for a typo fix."}
                </span>
              </div>
            </div>
          </div>

          {/* ── Content ── */}
          {!isFaq && (
            <div className="ds-form-section">
              <h5>Content</h5>
              <p className="ds-hint" style={{ marginTop: -4 }}>
                Links can only point to pages on {SITE}; any other link keeps its text but loses the link when saved.
                {path === "/shipment-policy" && (
                  <> Write <code>{"{{supportWhatsApp}}"}</code> where the WhatsApp support number should appear — the site fills in the current number.</>
                )}
              </p>
              <div className="cp-lang-grid">
                <div className="ds-form-group">
                  <label>English</label>
                  <RichTextField value={form.body_html} onChange={set("body_html")} readOnly={!editable || saving} />
                </div>
                <div className="ds-form-group">
                  <label>Arabic</label>
                  <RichTextField
                    rtl
                    readOnly={!editable || saving}
                    value={form.ar_body_html}
                    onChange={set("ar_body_html")}
                    placeholder="يُعرض النص الإنجليزي إذا تُرك فارغًا"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Search appearance ── */}
          <div className="ds-form-section">
            <h5>Search appearance</h5>
            <p className="ds-hint" style={{ marginTop: -4 }}>
              Leave these empty to use the automatic text shown in grey.
            </p>
            <div className="cp-lang-grid">
              {[
                { lang: "en", title: "seo_title", desc: "meta_description", label: "English", rtl: false },
                { lang: "ar", title: "ar_seo_title", desc: "ar_meta_description", label: "Arabic", rtl: true },
              ].map(({ lang, title, desc, label, rtl }) => (
                <div key={lang}>
                  <div className="ds-form-group">
                    <div className="cp-field-head">
                      <label htmlFor={`cp-${title}`}>SEO title ({label})</label>
                      <Counter value={form[title] || preview[lang]?.title} max={SEO_SOFT_LIMITS.title} />
                    </div>
                    <input
                      id={`cp-${title}`}
                      type="text"
                      dir={rtl ? "rtl" : undefined}
                      value={form[title]}
                      onChange={setFromInput(title)}
                      placeholder={preview[lang]?.title || ""}
                      maxLength={255}
                    />
                  </div>
                  <div className="ds-form-group">
                    <div className="cp-field-head">
                      <label htmlFor={`cp-${desc}`}>Meta description ({label})</label>
                      <Counter value={form[desc] || preview[lang]?.description} max={SEO_SOFT_LIMITS.description} />
                    </div>
                    <textarea
                      id={`cp-${desc}`}
                      rows={3}
                      dir={rtl ? "rtl" : undefined}
                      value={form[desc]}
                      onChange={setFromInput(desc)}
                      placeholder={preview[lang]?.description || ""}
                      maxLength={500}
                    />
                  </div>
                  <GooglePreview
                    locale={lang}
                    path={path}
                    rtl={rtl}
                    title={form[title] || preview[lang]?.title}
                    description={form[desc] || preview[lang]?.description}
                  />
                </div>
              ))}
            </div>
          </div>

          {editable && (
            <div className="ds-form-actions">
              {dirty && <span className="cp-dirty-note">Unsaved changes</span>}
              <button type="button" className="ds-btn-secondary" onClick={handleBack}>
                Cancel
              </button>
              <button type="submit" className="ds-btn-primary" disabled={saving || !dirty}>
                <Save size={15} style={{ marginInlineEnd: 6 }} />
                {saving ? "Saving..." : dirty ? (isFaq ? "Save page details" : "Save page") : "Saved"}
              </button>
            </div>
          )}
        </fieldset>
      </form>

      {isFaq && (
        <FaqTreeEditor editable={editable} onDirtyChange={setFaqDirty} onSaved={handleFaqSaved} />
      )}
    </div>
  );
};

export default ContentPageForm;
